(function () {
  // On a back/forward (traverse) load, the browser's native scroll
  // restoration can end up looking animated instead of instant, from two
  // separate sources: the global `scroll-behavior: smooth` rule in
  // assets/base.css (html selector) can leak into the restore/into
  // post-restore corrections in some engines; and CSS scroll anchoring
  // (overflow-anchor) can nudge the scroll offset as lazy images/cards
  // above the fold resize while loading. Either shows up as a visible
  // drift/slide instead of a straight cut to the saved position. We
  // neutralize both for the duration of THIS load only, then hand them back
  // to normal once the page has settled.
  // Checked via the standard Performance Navigation Timing API (not the
  // Navigation API used by isTraverseNavigation() below) specifically
  // because it also works in Safari, which has no View Transitions/
  // Navigation API support at all — this fix must not depend on either.
  // Runs unconditionally at the top of this file (not inside the
  // pageswap/pagereveal handlers below), so it applies even in browsers
  // that never fire those events.
  if (isBackForwardLoad()) {
    suppressSmoothScrollDuringRestore();
  }

  const viewTransitionRenderBlocker = document.getElementById('view-transition-render-blocker');
  // Remove the view transition render blocker if the user has reduced motion enabled or is on a low power device.
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches || isLowPowerDevice()) {
    viewTransitionRenderBlocker?.remove();
  } else {
    // If the browser didn't manage to parse the main content quickly, at least let the user see something.
    // We're aiming for the FCP to be under 1.8 seconds since the navigation started.
    const RENDER_BLOCKER_TIMEOUT_MS = Math.max(0, 1800 - performance.now());

    setTimeout(() => {
      viewTransitionRenderBlocker?.remove();
    }, RENDER_BLOCKER_TIMEOUT_MS);
  }

  const idleCallback = typeof requestIdleCallback === 'function' ? requestIdleCallback : setTimeout;

  window.addEventListener('pageswap', async (event) => {
    const { viewTransition } = /** @type {PageSwapEvent} */ (event);

    if (shouldSkipViewTransition(viewTransition, event)) {
      /** @type {ViewTransition | null} */ (viewTransition)?.skipTransition();
      return;
    }

    // Cancel view transition on user interaction to improve INP (Interaction to Next Paint)
    ['pointerdown', 'keydown'].forEach((eventName) => {
      document.addEventListener(
        eventName,
        () => {
          viewTransition.skipTransition();
        },
        { once: true }
      );
    });

    // Clean in case you landed on the pdp first. We want to remove the default transition type on the PDP media gallery so there is no duplicate transition name
    document
      .querySelectorAll('[data-view-transition-type]:not([data-view-transition-triggered])')
      .forEach((element) => {
        element.removeAttribute('data-view-transition-type');
      });

    const transitionTriggered = document.querySelector('[data-view-transition-triggered]');
    const transitionType = transitionTriggered?.getAttribute('data-view-transition-type');

    if (transitionType) {
      viewTransition.types.clear();
      viewTransition.types.add(transitionType);
      sessionStorage.setItem('custom-transition-type', transitionType);
    } else {
      viewTransition.types.clear();
      viewTransition.types.add('page-navigation');
      sessionStorage.removeItem('custom-transition-type');
    }
  });

  window.addEventListener('pagereveal', async (event) => {
    const { viewTransition } = /** @type {PageRevealEvent} */ (event);

    if (shouldSkipViewTransition(viewTransition, event)) {
      /** @type {ViewTransition | null} */ (viewTransition)?.skipTransition();
      return;
    }

    const customTransitionType = sessionStorage.getItem('custom-transition-type');

    if (customTransitionType) {
      viewTransition.types.clear();
      viewTransition.types.add(customTransitionType);

      await viewTransition.finished;

      viewTransition.types.clear();
      viewTransition.types.add('page-navigation');

      idleCallback(() => {
        sessionStorage.removeItem('custom-transition-type');
        document.querySelectorAll('[data-view-transition-type]').forEach((element) => {
          element.removeAttribute('data-view-transition-type');
        });
      });
    } else {
      viewTransition.types.clear();
      viewTransition.types.add('page-navigation');
    }
  });

  /**
   * @param {ViewTransition | null} viewTransition
   * @param {PageSwapEvent | PageRevealEvent} event
   * @returns {viewTransition is null}
   */
  function shouldSkipViewTransition(viewTransition, event) {
    return !(viewTransition instanceof ViewTransition) || isLowPowerDevice() || isTraverseNavigation(event);
  }

  /**
   * Back/forward (browser history "traverse") navigations restore the
   * native scroll position AFTER the view transition's "new page" snapshot
   * is captured at scroll position 0. That ordering is what causes the
   * visible "jump to top, then snap to the saved scroll position" on
   * back/forward. Skipping the transition for traverse navigations lets the
   * browser paint directly at the restored scroll position, with no
   * snapshot race. Regular link clicks (push navigations) and reloads are
   * untouched and keep their normal behavior/crossfade.
   * @param {PageSwapEvent | PageRevealEvent} event
   * @returns {boolean}
   */
  function isTraverseNavigation(event) {
    const navigationType =
      /** @type {PageSwapEvent} */ (event).activation?.navigationType ??
      /** @type {Navigation} */ (window.navigation)?.activation?.navigationType;
    return navigationType === 'traverse';
  }

  /**
   * @returns {boolean}
   */
  function isBackForwardLoad() {
    const navEntry = performance.getEntriesByType('navigation')[0];
    return navEntry?.type === 'back_forward';
  }

  /**
   * Overrides `scroll-behavior` to 'auto' and `overflow-anchor` to 'none' via
   * inline style on <html> — inline style beats the base.css rule regardless
   * of specificity (that rule has no !important), so no stylesheet needs
   * touching. The second override (overflow-anchor) stops the browser's
   * scroll anchoring: as lazy images/cards above the fold finish loading and
   * resize after the restore, the browser would otherwise auto-compensate
   * the scroll offset to keep content in place, which shows up as a small
   * drift instead of the page staying put. Page-internal smooth anchor
   * scrolling is unaffected because both are removed again well before the
   * user could trigger one: `load` + a fixed buffer so any still-settling
   * layout doesn't fight back once we hand things back to normal.
   */
  function suppressSmoothScrollDuringRestore() {
    const html = document.documentElement;
    html.style.scrollBehavior = 'auto';
    html.style.overflowAnchor = 'none';

    const restoreNormalScrollBehavior = () => {
      html.style.scrollBehavior = '';
      html.style.overflowAnchor = '';
    };

    if (document.readyState === 'complete') {
      setTimeout(restoreNormalScrollBehavior, 400);
    } else {
      window.addEventListener('load', () => setTimeout(restoreNormalScrollBehavior, 400), { once: true });
    }
  }

  /*
   * We can't import this logic from utilities.js here, but we should keep them in sync.
   */
  function isLowPowerDevice() {
    /* Skip ESLint compatibility check. Number(undefined) <= 2 is always false anyway. */
    /* eslint-disable-next-line compat/compat */
    return Number(navigator.hardwareConcurrency) <= 2 || Number(navigator.deviceMemory) <= 2;
  }
})();
